'use client';

import { useState, useEffect, useRef } from 'react';
import { MapPin, Clock, Users, ChevronRight, HandHeart } from 'lucide-react';
import { ResponsiveSheet, SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import type { StandaloneOpportunity } from '@/types/serve-finder';
import { Img } from '@/lib/asset-url';
import { BaseCard } from './BaseCard';
import { CardHero } from './CardHero';
import { CardCTA } from './CardCTA';

interface ServingGroupCardProps {
  opportunities: StandaloneOpportunity[];
}

const MINI_CARD_WIDTH = 160; // px per mini-card
const MINI_CARD_GAP = 12; // gap between cards
const AUTO_SCROLL_INTERVAL = 4000; // ms

/** Single grid card that groups all ongoing serving opportunities */
export function ServingGroupCard({ opportunities }: ServingGroupCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollPos, setScrollPos] = useState(0);

  // Auto-scroll horizontally
  useEffect(() => {
    if (opportunities.length <= 1) return;
    const step = MINI_CARD_WIDTH + MINI_CARD_GAP;
    const totalWidth = opportunities.length * step;

    const interval = setInterval(() => {
      setScrollPos((prev) => {
        const next = prev + step;
        // Loop back when we've scrolled past all items
        return next >= totalWidth ? 0 : next;
      });
    }, AUTO_SCROLL_INTERVAL);
    return () => clearInterval(interval);
  }, [opportunities.length]);

  // Apply scroll position
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({ left: scrollPos, behavior: 'smooth' });
  }, [scrollPos]);

  if (opportunities.length === 0) return null;

  const heroImage = '/Ongoing-Serve-Opps/hero.svg';

  return (
    <>
      <BaseCard
        variant="standard"
        className="bg-card cursor-pointer text-left"
        onClick={() => setIsOpen(true)}
        header={
          <CardHero bgUrl={heroImage}>
            {/* Opportunity count strip */}
            <div className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-center gap-1.5 bg-gradient-to-t from-black/60 to-transparent px-4 pt-4 pb-2 text-[11px] font-semibold tracking-widest text-white/80 uppercase">
              <HandHeart className="h-3 w-3" />
              {opportunities.length} Opportunities
            </div>
          </CardHero>
        }
        body={
          <div className="relative overflow-hidden py-3">
            {/* Horizontal scrolling carousel */}
            <div
              ref={scrollRef}
              className="flex gap-3 overflow-x-hidden px-3"
              style={{ scrollbarWidth: 'none' }}
            >
              {opportunities.map((o) => (
                <MiniCard key={o.ID} opportunity={o} />
              ))}
            </div>
            {/* Fade edges */}
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-4 bg-gradient-to-r from-[hsl(var(--card))] to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-4 bg-gradient-to-l from-[hsl(var(--card))] to-transparent" />
          </div>
        }
        footer={<CardCTA label="See All" useDefaultColors />}
      />

      <ResponsiveSheet
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="max-w-3xl"
        noPanelPadding
        panelClassName="bg-card"
        header={
          <div className="relative">
            <div className="relative aspect-[3/1] w-full overflow-hidden">
              <Img
                src={heroImage}
                alt="Ongoing Serve Opportunities"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="border-border bg-card border-b px-4 py-3">
              <h2 className="text-foreground text-lg leading-tight font-extrabold sm:text-xl">
                Ongoing Needs
              </h2>
              <p className="text-muted-foreground mt-0.5 text-xs">
                Find a place to use your gifts and serve others.
              </p>
            </div>
          </div>
        }
      >
        <SheetPage name="main">
          <div className="space-y-3 p-4">
            {opportunities.map((o) => (
              <OpportunityRow key={o.ID} opportunity={o} />
            ))}
          </div>
        </SheetPage>
      </ResponsiveSheet>
    </>
  );
}

// ---------------------------------------------------------------------------
// MiniCard — stacked layout (image on top, title + ministry below)
// ---------------------------------------------------------------------------

function MiniCard({ opportunity }: { opportunity: StandaloneOpportunity }) {
  return (
    <div
      className="border-border flex w-40 shrink-0 flex-col overflow-hidden border"
      style={{ width: MINI_CARD_WIDTH }}
    >
      <div className="aspect-video w-full overflow-hidden">
        <Img src={opportunity.Image_URL} alt="" className="h-full w-full object-cover" />
      </div>
      <div className="flex min-w-0 flex-col p-2">
        <h4 className="text-foreground truncate text-xs leading-tight font-bold">
          {opportunity.Title}
        </h4>
        <p className="text-muted-foreground mt-0.5 truncate text-[10px] font-semibold tracking-wide uppercase">
          {opportunity.Ministry_Name}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Opportunity Row
// ---------------------------------------------------------------------------

function OpportunityRow({ opportunity }: { opportunity: StandaloneOpportunity }) {
  return (
    <a
      href={opportunity.Sign_Up_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="group border-border bg-background flex items-center gap-3 border p-3 transition-shadow hover:shadow-md"
    >
      {/* Thumbnail */}
      <div className="h-12 w-20 shrink-0 overflow-hidden">
        <Img
          src={opportunity.Image_URL}
          alt={opportunity.Title}
          className="h-full w-full object-cover"
        />
      </div>

      {/* Title + facts */}
      <div className="min-w-0 flex-1">
        <h4 className="text-foreground text-sm leading-tight font-bold">{opportunity.Title}</h4>
        <p className="text-muted-foreground mt-0.5 text-xs">
          {opportunity.Congregation_Name} · {opportunity.Time}
        </p>
      </div>

      {/* Volunteers badge + chevron */}
      <div className="flex shrink-0 items-center gap-2">
        {opportunity.Volunteers_Needed != null && (
          <span className="inline-flex items-center gap-1 rounded-full bg-[var(--brand-secondary)] px-2 py-0.5 text-[10px] font-bold text-white">
            <Users className="h-3 w-3" />
            {opportunity.Volunteers_Needed}
          </span>
        )}
        <ChevronRight className="text-muted-foreground h-4 w-4" />
      </div>
    </a>
  );
}
